import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { capitalize as _capitalize } from 'lodash';
import PropTypes from 'prop-types';
import HorizontalRule from '../HorizontalRule';
import handleKeyPress from '../../common/handleKeyPress';
import { setApplicationFocus } from '../../actions/browser';
import APPLICATION_FOCUS from '../../constants/applicationFocus';
import * as Styled from '../styles';

const MAX_VALUE_MESSAGE = 'Maximum value is 50';
const MIN_VALUE_MESSAGE = 'Minimum value is 0';
// TODO: Use the Inpit Component
const Input = ({ padding, direction, onChange, id, border }) => {
    // FontAwesome's arrow-from-{direction} icons' directions refer to where they're coming from,
    // so we need to flip the direction.
    const DIRECTIONS = {
        left: 'right',
        right: 'left',
        top: 'bottom',
        bottom: 'top',
    };
    return (
        <Styled.DirectionLabel htmlFor={id}>
            {_capitalize(direction)}
            <div
                style={{
                    width: 86,
                    height: 28,
                    marginTop: 4,
                    boxSizing: 'border-box',
                    border,
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Styled.DirectionIcon size="1x" role="img" icon={['far', `arrow-from-${DIRECTIONS[direction]}`]} />
                <div style={{ display: 'flex', minWidth: 0 }} data-test-id={`${id}-padding`}>
                    <input
                        type="number"
                        min={0}
                        max={50}
                        id={id}
                        style={{
                            border: 'none',
                            color: '#1C1C1C',
                            textAlign: 'center',
                            outline: 'none',
                            fontWeight: 400,
                            fontSize: 13,
                            minWidth: 0,
                        }}
                        onChange={onChange}
                        value={padding}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        marginRight: 4,
                        width: 12,
                    }}
                >
                    <Styled.ChangePaddingIcon
                        icon="sort-up"
                        id={`${id}-increase`}
                        onClick={(e) => onChange(e, 'increase', id)}
                        onKeyDown={(e) => handleKeyPress(e, () => onChange(e, 'increase', id))}
                        role="button"
                        tabIndex={0}
                        aria-label={`Increase element ${direction} padding`}
                        style={{ top: 4 }}
                        data-test-id={`${id}-padding-increase`}
                    />
                    <HorizontalRule styling={{ margin: 0, color: '#D9DADB' }} />
                    <Styled.ChangePaddingIcon
                        icon="sort-down"
                        id={`${id}-decrease`}
                        onClick={(e) => onChange(e, 'decrease', id)}
                        onKeyDown={(e) => handleKeyPress(e, () => onChange(e, 'decrease', id))}
                        role="button"
                        tabIndex={0}
                        aria-label={`Decrease element ${direction} padding`}
                        style={{ bottom: 4 }}
                        data-test-id={`${id}-padding-decrease`}
                    />
                </div>
            </div>
        </Styled.DirectionLabel>
    );
};

Input.propTypes = {
    padding: PropTypes.number.isRequired,
    direction: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    border: PropTypes.string,
};

Input.defaultProps = {
    border: '1px solid #CCCCCC',
};

const ElementPadding = ({ dispatch, onChange, paddingLeft, paddingRight, paddingTop, paddingBottom, elementType, applicationFocus }) => {
    const [errors, setErrors] = useState({
        paddingTop: '',
        paddingBottom: '',
        paddingRight: '',
        paddingLeft: '',
    });
    const ref = useRef(null);

    useEffect(() => {
        if (applicationFocus === APPLICATION_FOCUS.RIGHT_PANEL && ref && ref.current) {
            ref.current.focus();
            dispatch(setApplicationFocus(APPLICATION_FOCUS.NONE));
        }
    }, [applicationFocus, ref, dispatch]);

    const handleSingular = (event, type, paddingId) => {
        const { value, id } = event.target;
        const paddingValues = {
            paddingLeft,
            paddingRight,
            paddingTop,
            paddingBottom,
        };

        if (value) {
            if (value > 50) {
                const newErrors = { ...errors, [id]: MAX_VALUE_MESSAGE };
                setErrors(newErrors);
                return;
            }
            if (value < 0) {
                const newErrors = { ...errors, [id]: MIN_VALUE_MESSAGE };
                setErrors(newErrors);
                return;
            }
        } else {
            if (type === 'increase') {
                if (paddingValues[paddingId] >= 50) {
                    const newErrors = { ...errors, [paddingId]: MAX_VALUE_MESSAGE };
                    setErrors(newErrors);
                    return;
                }
            }
            if (type === 'decrease') {
                if (paddingValues[paddingId] <= 0) {
                    const newErrors = { ...errors, [paddingId]: MIN_VALUE_MESSAGE };
                    setErrors(newErrors);
                    return;
                }
            }
        }
        const clearErrors = { ...errors };
        Object.keys(clearErrors).forEach((val) => {
            clearErrors[val] = '';
        });

        setErrors(clearErrors);
        onChange(event);
    };

    const handleAll = (event, type) => {
        const paddingValues = [paddingLeft, paddingRight, paddingTop, paddingBottom];
        const allMin = paddingValues.every((val) => val === 0);
        const allMax = paddingValues.every((val) => val === 50);

        if (type === 'increase') {
            if (allMax) {
                const newErrors = { ...errors };
                Object.keys(newErrors).forEach((val) => {
                    newErrors[val] = MAX_VALUE_MESSAGE;
                });
                setErrors(newErrors);
                return;
            }
        }
        if (type === 'decrease') {
            if (allMin) {
                const newErrors = { ...errors };
                Object.keys(newErrors).forEach((val) => {
                    newErrors[val] = MIN_VALUE_MESSAGE;
                });
                setErrors(newErrors);
                return;
            }
        }

        const clearErrors = { ...errors };
        Object.keys(clearErrors).forEach((val) => {
            clearErrors[val] = '';
        });

        setErrors(clearErrors);
        onChange(event);
    };

    const error = errors[Object.keys(errors).find((key) => errors[key])];

    return (
        <div style={{ width: '100%' }}>
            <Styled.ElementPaddingDiv>
                <Styled.ElementPaddingHeader>
                    {elementType} spacing
                    <Styled.AllPaddingControllersDiv>
                        <Styled.PaddingIcon
                            id="minusAllPadding"
                            data-test-id="minusAllPadding"
                            role="button"
                            aria-label="Subtract 5px spacing to all sides"
                            onClick={(e) => handleAll(e, 'decrease')}
                            onKeyDown={(e) => handleKeyPress(e, () => handleAll(e, 'decrease'))}
                            tabIndex={0}
                            className="material-icons"
                            ref={ref}
                        >
                            remove_circle
                        </Styled.PaddingIcon>
                        <Styled.VerticalLine />
                        <Styled.PaddingIcon
                            id="plusAllPadding"
                            data-test-id="plusAllPadding"
                            role="button"
                            aria-label="Add 5px spacing to all sides"
                            onClick={(e) => handleAll(e, 'increase')}
                            onKeyDown={(e) => handleKeyPress(e, () => handleAll(e, 'increase'))}
                            tabIndex={0}
                            className="material-icons"
                        >
                            add_circle
                        </Styled.PaddingIcon>
                    </Styled.AllPaddingControllersDiv>
                </Styled.ElementPaddingHeader>
                <Styled.PaddingControlsContainer>
                    <Input
                        id="paddingLeft"
                        direction="left"
                        onChange={handleSingular}
                        padding={paddingLeft}
                        border={errors.paddingLeft ? '1px solid #E82E41' : undefined}
                    />
                    <Input
                        id="paddingRight"
                        direction="right"
                        onChange={handleSingular}
                        padding={paddingRight}
                        border={errors.paddingRight ? '1px solid #E82E41' : undefined}
                    />
                    <Input
                        id="paddingTop"
                        direction="top"
                        onChange={handleSingular}
                        padding={paddingTop}
                        border={errors.paddingTop ? '1px solid #E82E41' : undefined}
                    />
                    <Input
                        id="paddingBottom"
                        direction="bottom"
                        onChange={handleSingular}
                        padding={paddingBottom}
                        border={errors.paddingBottom ? '1px solid #E82E41' : undefined}
                    />
                </Styled.PaddingControlsContainer>
            </Styled.ElementPaddingDiv>
            {error && (
                <div style={{ color: '#D32F2F', fontSize: 10, fontWeight: 600, marginBottom: 2 }}>{error}</div>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    applicationFocus: state.browser.applicationFocus,
});

ElementPadding.propTypes = {
    dispatch: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    paddingLeft: PropTypes.number.isRequired,
    paddingRight: PropTypes.number.isRequired,
    paddingTop: PropTypes.number.isRequired,
    paddingBottom: PropTypes.number.isRequired,
    elementType: PropTypes.oneOf(['button', 'image', 'paragraph', 'social', 'survey', 'video', 'multiple']).isRequired,
    applicationFocus: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(ElementPadding);
